import { ArrowRight, CircleDollarSign, Gift, WalletCards } from 'lucide-react';
import { useMemo, useState } from 'react';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import Button from 'src/components/Button/Button';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import Modal from 'src/components/Modal/Modal';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserGamificationDetailsContext } from 'src/contexts/UserGamificationDetailsContext/UserGamificationDetailsContext';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { convertLocalCurrencyToPoints } from 'src/utils/local-currency';

const CONVERSION_EXAMPLE_POINTS = 20;

const PointsToCashConversionNotice: React.FC = () => {
  const t = useTranslationByKey('COMPONENTS.POINTS_TO_CASH_CONVERSION_NOTICE');
  const { accountDetails } = useUserAccountDetailsContext();
  const { reward } = useUserGamificationDetailsContext();
  const { userSettings, userSettingsLoaded, saveUserSettings } = useUserSettingsContext();
  const { currencyCode, formatCurrency, formatPoints } = useUserCurrency();
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [dismissedForSession, setDismissedForSession] = useState(false);

  const isOpen =
    userSettingsLoaded &&
    userSettings?.showPointsToCashConversionNotice === true &&
    accountDetails !== null &&
    !dismissedForSession;

  const conversionValue = formatPoints(CONVERSION_EXAMPLE_POINTS);
  const conversionValueWithCode = conversionValue.toUpperCase().includes(currencyCode.toUpperCase())
    ? conversionValue
    : `${conversionValue} ${currencyCode}`;

  const rewardTargetPoints = useMemo(
    () =>
      reward
        ? convertLocalCurrencyToPoints(reward.amount, reward.usdExchangeRate ?? accountDetails?.usdExchangeRate ?? 1)
        : 0,
    [accountDetails?.usdExchangeRate, reward]
  );
  const currentBalancePoints = accountDetails?.currentPointsBalance ?? 0;
  const rewardProgressPercent = rewardTargetPoints
    ? Math.min(100, Math.max(0, (currentBalancePoints / rewardTargetPoints) * 100))
    : 0;

  const acknowledgeAndClose = async () => {
    if (isAcknowledging) {
      return;
    }

    setIsAcknowledging(true);
    try {
      await saveUserSettings(UserSettingType.ShowPointsToCashConversionNotice, 'false');
    } finally {
      setDismissedForSession(true);
      setIsAcknowledging(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={acknowledgeAndClose}
      hideCloseButton
      closeOnBackdropClick={false}
      overlayClassName="!z-[10000]"
      className="box-content !w-[calc(100%_-_2rem)] !max-w-[520px] !rounded-[30px] !bg-transparent !shadow-none"
      modalBodyClassName="!items-stretch !overflow-y-auto !rounded-[30px] !border !border-violet-100 !bg-[#FBF9FF] !p-0 !shadow-[0_32px_90px_rgba(76,58,140,0.22)]"
    >
      <div className="flex w-full flex-col">
        <div className="relative overflow-hidden border-b border-violet-100 bg-white px-5 py-6 md:px-7">
          <div className="absolute -right-12 -top-14 h-36 w-36 rounded-full bg-violet-100/70 blur-2xl" />
          <div className="relative flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-[#6C4DD9]">
              <CircleDollarSign className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <p className="font-manrope text-xs font-semibold uppercase tracking-[0.16em] text-[#7C5CFA]">
                {t('EYEBROW')}
              </p>
              <h2 className="mt-1 font-zillaSlab text-2xl font-semibold leading-tight text-slate-900 md:text-[28px]">
                {t('TITLE')}
              </h2>
            </div>
          </div>
          <p className="relative mt-4 font-manrope text-sm leading-6 text-slate-600">{t('INTRO')}</p>
        </div>

        <div className="flex flex-col gap-4 p-5 md:p-7">
          <section
            className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4"
            aria-label={t('CONVERSION_LABEL')}
          >
            <p className="font-manrope text-xs font-semibold uppercase tracking-[0.12em] text-violet-700">
              {t('CONVERSION_LABEL')}
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="rounded-xl bg-white px-3 py-2 font-spaceGrotesk text-base font-semibold text-slate-700 shadow-sm">
                {t('POINTS_VALUE', { points: CONVERSION_EXAMPLE_POINTS })}
              </span>
              <ArrowRight className="h-5 w-5 shrink-0 text-violet-500" aria-hidden="true" />
              <span className="rounded-xl bg-[#6C4DD9] px-3 py-2 text-right font-spaceGrotesk text-base font-semibold text-white shadow-sm">
                {conversionValueWithCode}
              </span>
            </div>
          </section>

          <section className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.05)]">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <WalletCards className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="font-manrope text-xs font-medium text-slate-500">
                  {t('BALANCE_LABEL', { currency: currencyCode })}
                </p>
                <p className="font-spaceGrotesk text-xl font-semibold text-slate-900">
                  {formatPoints(currentBalancePoints)}
                </p>
              </div>
            </div>
            <p className="mt-3 font-manrope text-xs leading-5 text-slate-500">
              {t('BALANCE_HELP', { currency: currencyCode })}
            </p>
          </section>

          <section className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-amber-700 shadow-sm">
                <Gift className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-zillaSlab text-lg font-semibold text-slate-900">{t('REWARDS_TITLE')}</h3>
                <p className="mt-1 font-manrope text-xs leading-5 text-slate-600">{t('REWARDS_BODY')}</p>

                {reward ? (
                  <div className="mt-3 rounded-xl bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-manrope text-[11px] font-medium uppercase tracking-wide text-slate-500">
                          {t('SELECTED_REWARD')}
                        </p>
                        <p className="mt-0.5 truncate font-spaceGrotesk text-sm font-semibold text-slate-800">
                          {formatCurrency(reward.amount, reward.currencyCode)} {reward.name}
                        </p>
                      </div>
                      <p className="shrink-0 font-spaceGrotesk text-xs font-semibold text-violet-700">
                        {t('REWARD_PROGRESS')}
                      </p>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#7C5CFA] to-[#4F9CD9]"
                        style={{ width: `${rewardProgressPercent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex justify-between gap-2 font-spaceGrotesk text-xs text-slate-600">
                      <span>{formatPoints(Math.min(currentBalancePoints, rewardTargetPoints))}</span>
                      <span>{formatPoints(rewardTargetPoints)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 rounded-xl bg-white p-3 font-manrope text-xs leading-5 text-slate-600 shadow-sm">
                    {t('NO_REWARD')}
                  </p>
                )}
              </div>
            </div>
          </section>

          <Button
            label={t('BUTTON')}
            onClick={() => void acknowledgeAndClose()}
            state={isAcknowledging ? ButtonState.Disabled : ButtonState.Enabled}
            isLoading={isAcknowledging}
            type={ButtonType.Filled}
            animated={false}
            interactive={false}
            className="mt-1 !h-12 w-full !rounded-2xl !border-0 !bg-[#6C4DD9] !text-white !shadow-[0_16px_30px_rgba(108,77,217,0.24)]"
          />
        </div>
      </div>
    </Modal>
  );
};

export default PointsToCashConversionNotice;
